import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { Box, Button, Typography, Tabs, Tab, TextField } from "@mui/material";
import axios from "axios";
import ApproveVendorServiceForm from "./approveVendorserviceform";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const VendorServices = () => {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [bookings, setBookings] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [isUpdatePopupOpen, setIsUpdatePopupOpen] = useState(false); // State for update popup

  // Fetch bookings data
  useEffect(() => {
    const fetchBookings = async () => {
      try {
        const response = await axios.get(
          `${BASE_URL}/api/v1/VendorManagement/show-AllVendorService`
        );
        const fetchedBookings =
          response.data?.data?.allVendorService?.map((service, index) => ({
            ...service,
            id: service.id || index,
            firstName: service.Vendor?.firstName || "N/A",
            lastName: service.Vendor?.lastName || "N/A",
            email: service.Vendor?.email || "N/A",
            mobileNumber: service.Vendor?.mobile_number || "N/A",
            CompanyName: service.CompanyName || "N/A",
            city: service.City || "N/A",
            ServiceName: service.ServiceName || "N/A",
          })) || [];
        setBookings(fetchedBookings);
      } catch (error) {
        console.error("Error fetching vendor data:", error);
      }
    };

    fetchBookings();
  }, []);

  // Filtered data based on searchText and tabValue
  const filteredBookings = useMemo(() => {
    let data = bookings.filter((booking) =>
      booking.ServiceName?.toLowerCase().includes(searchText.toLowerCase())
    );

    if (tabValue === 0)
      return data.filter((booking) => booking.status === "pending");
    if (tabValue === 1)
      return data.filter((booking) => booking.status === "approved");
    if (tabValue === 2)
      return data.filter((booking) => booking.status === "rejected");

    return data;
  }, [bookings, searchText, tabValue]);

  // Handle search input changes
  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Handle opening the verification form
  const handleVerify = (vendor) => {
    setSelectedVendor(vendor);
    setIsPopupOpen(true);
  };

  // Delete Vendor Service by ID
  const handleDelete = async (vendorId) => {
    if (
      !window.confirm("Are you sure you want to delete this vendor service?")
    ) {
      return; // Confirm before deleting
    }

    try {
      await axios.delete(
        `${BASE_URL}/api/v1/VendorManagement/Delete-Vendor-Service/${vendorId}`
      );
      console.log(`Vendor service with ID ${vendorId} deleted successfully`);

      // Remove the deleted vendor from the UI
      setBookings((prevBookings) =>
        prevBookings.filter((booking) => booking.id !== vendorId)
      );
    } catch (error) {
      console.error("Error deleting vendor service:", error);
      alert("Failed to delete vendor service. Please try again.");
    }
  };

  // Handle closing the verification form
  const handleClosePopup = () => {
    setIsPopupOpen(false);
    setSelectedVendor(null);
  };

  // Open the update form
  const handleUpdate = (vendor) => {
    setSelectedVendor(vendor); // Set the selected vendor
    setIsUpdatePopupOpen(true); // Open the update popup
  };

  // Close the update form
  const handleCloseUpdatePopup = () => {
    setIsUpdatePopupOpen(false); // Close the update popup
    setSelectedVendor(null); // Reset selected vendor
  };

  const HandleBack = () => {
    console.log("Back button clicked");
    navigate("/admin-dashboard");
  };

  // Handle update form submission
  // const handleUpdateSubmit = async (updatedVendor) => {
  //   try {
  //     // Call API to update the vendor
  //     const response = await axios.post(
  //       `${BASE_URL}/api/v1/VendorManagement/updateVendorService`,
  //       updatedVendor
  //     );
  //     console.log("Vendor updated successfully:", response.data);

  //     // Update the state for bookings
  //     setBookings((prev) => {
  //       const updatedBookings = [...prev];
  //       const index = updatedBookings.findIndex(
  //         (b) => b.id === updatedVendor.id
  //       );
  //       if (index !== -1) {
  //         updatedBookings[index] = {
  //           ...updatedBookings[index],
  //           ...updatedVendor, // Update with new data
  //         };
  //       }
  //       return updatedBookings;
  //     });

  //     // Close the popup
  //     handleCloseUpdatePopup();
  //   } catch (error) {
  //     console.error("Error updating vendor:", error);
  //   }
  // };

  // Handle vendor approval form submission
  const handleApproveSubmit = async (updatedVendor, action) => {
    // Optimistically update state before API call
    setBookings((prev) => {
      const updatedBookings = [...prev];
      const index = updatedBookings.findIndex((b) => b.id === updatedVendor.id);
      if (index !== -1) {
        updatedBookings[index] = {
          ...updatedBookings[index],
          status: action,
          SpecialBadges: action, // Set status based on the action ("approved", "rejected", or "pending")
        };
      }
      return updatedBookings;
    });

    try {
      // Call API to update the vendor
      const response = await axios.post(
        `${BASE_URL}/api/v1/VendorManagement/UpdateVendorService`,
        { ...updatedVendor, status: action, SpecialBadges: action } // Include the updated status in the API call
      );
      console.log("Vendor updated successfully:", response.data);

      // The state is already updated optimistically, so no further action needed
    } catch (error) {
      console.error("Error updating vendor:", error);

      // Optional: Revert the optimistic update if the API call fails
      setBookings((prev) => {
        const updatedBookings = [...prev];
        const index = updatedBookings.findIndex(
          (b) => b.id === updatedVendor.id
        );
        if (index !== -1) {
          updatedBookings[index] = {
            ...updatedBookings[index],
            status: updatedVendor.status || "pending",
            SpecialBadges: updatedVendor.SpecialBadges, // Revert to the original status
          };
        }
        return updatedBookings;
      });
    }

    // Close the popup
    handleClosePopup();
  };

  // DataGrid columns
  const columns = [
    { field: "firstName", headerName: "Vendor Name", flex: 1 },
    { field: "email", headerName: "Email", flex: 1.2 },
    { field: "mobileNumber", headerName: "Mobile Number", flex: 1.2 },
    { field: "CompanyName", headerName: "Company Name", flex: 1 },
    { field: "city", headerName: "City", flex: 1 },
    { field: "ServiceName", headerName: "Category", flex: 1.5 },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            color:
              params.value === "approved"
                ? "green"
                : params.value === "pending"
                ? "orange"
                : "red",
          }}
        >
          {params.value.charAt(0).toUpperCase() + params.value.slice(1)}
        </Typography>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: (params) => {
        const vendor = params.row;

        const handleUpdate = () => {
          // Logic for updating vendor
          console.log("Update vendor:", vendor);
        };

        if (tabValue === 0) {
          // Pending Tab
          return (
            <Box display="flex" gap={1}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleVerify(vendor)}
              >
                Verify
              </Button>
            </Box>
          );
        } else if (tabValue === 1) {
          // Approved Tab
          return (
            <Box display="flex" gap={1}>
              <Button
                variant="contained"
                sx={{ backgroundColor: "green", color: "#fff" }}
                onClick={() => handleVerify(vendor)}
              >
                Update
              </Button>
            </Box>
          );
        } else if (tabValue === 2) {
          // Rejected Tab
          return (
            <Box display="flex" gap={1}>
              {/* <Button variant="contained" color="error" onClick={handleDelete}>
                Delete
              </Button> */}
              <Button
                variant="contained"
                color="secondary"
                onClick={() => handleVerify(vendor)} // Open update popup
              >
                Retrieve
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => handleDelete(vendor.id)} // Trigger deletion
              >
                Delete
              </Button>
            </Box>
          );
        }
        return null;
      },
    },
  ];

  return (
    <>
      <Box
        sx={{
          width: "90%",
          margin: "5% auto",
          padding: 2,
          backgroundColor: "#f5f5f5",
          borderRadius: "12px",
        }}
      >
        {/* Back Button */}
        <Button
          variant="contained"
          color="secondary"
          onClick={HandleBack}
          sx={{ position: "absolute", top: "20px", left: "20px" }}
        >
          Back
        </Button>
        <Typography
          variant="h4"
          align="center"
          gutterBottom
          sx={{ fontWeight: "bold", marginBottom: 3 }}
        >
          Vendor Services
        </Typography>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          centered
        >
          <Tab label="Pending" />
          <Tab label="Approved" />
          <Tab label="Rejected" />
        </Tabs>
        <Box mb={3} display="flex" justifyContent="center" alignItems="center">
          <TextField
            variant="outlined"
            size="medium"
            label="Search by Service Name"
            value={searchText}
            onChange={handleSearchChange}
            sx={{
              width: "50%",
              backgroundColor: "#ffffff",
              borderRadius: "4px",
            }}
          />
        </Box>
        <Box sx={{ height: 600 }}>
          <DataGrid
            rows={filteredBookings}
            columns={columns}
            pageSize={8}
            rowsPerPageOptions={[8, 12, 16]}
            disableRowSelectionOnClick
          />
        </Box>
      </Box>

      {/* Approve vendor service popup */}
      {isPopupOpen && (
        <ApproveVendorServiceForm
          open={isPopupOpen}
          onClose={handleClosePopup}
          vendorData={selectedVendor}
          onSubmit={handleApproveSubmit} // Pass updated vendor back to parent
        />
      )}

      {/* Update vendor service popup */}
      {/* {isUpdatePopupOpen && (
        <updateVendorserviceform
          open={isUpdatePopupOpen}
          onClose={handleCloseUpdatePopup}
          vendorData={selectedVendor}
          onSubmit={handleUpdateSubmit} // Pass updated vendor back to parent
        />
      )} */}
    </>
  );
};

export default VendorServices;
